import {useCallback, useEffect, useMemo, useState} from 'react';

import {processorService} from '@/services/processorService';
import type {AfriexProcessor} from '@/types';
import {logger} from '@/utils/logger';

import {getProductionStatus} from './productionStatus';
import type {ProcessorListProps, ProductionStatusFilter} from './types';

const PAGE_SIZE = 10;
const FETCH_SIZE = 100;

const statusOf = (processor: AfriexProcessor): ProductionStatusFilter =>
  getProductionStatus(processor.meta?.capabilityStatuses) ?? 'UNKNOWN';

/**
 * The whole processor catalogue is small (one record per processor node), so it
 * is loaded once and searched, filtered and paged in the browser, the same way
 * Settings -> Currencies does.
 */
const useProcessorList = (): ProcessorListProps => {
  const [fullList, setFullList] = useState<AfriexProcessor[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(null);
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] =
    useState<ProductionStatusFilter>('ALL');

  const fetchList = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);
      // Downloading the whole collection is deliberate here: there is one row
      // per processor (`code` is limited to AfriexTransactionProcessor, well
      // under 150), so this loop runs once or twice.
      //
      // Do not copy this loop for a collection that grows (transactions, fees,
      // users, audit events). Every page re-runs the server's count and a
      // longer skip, so each page costs more than the last, and the browser
      // ends up holding every row. For those, page on the server and render
      // one page at a time, stream the full range from one cursor (the
      // `?action=DOWNLOAD` exports built on streamCursorToConsumer), or, in a
      // server job, read in `_id` batches the way BatchTransactionProcessor
      // does.
      const byKey = new Map<string, AfriexProcessor>();
      for (let pageIndex = 0; ; pageIndex++) {
        const {data = [], total = 0} = await processorService.getProcessorList({
          page: pageIndex,
          limit: FETCH_SIZE,
        });
        data.forEach((item) => byKey.set(item.id || item.code, item));
        if (byKey.size >= total || data.length < FETCH_SIZE) {
          break;
        }
      }
      setFullList(
        [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name)),
      );
    } catch (e: unknown) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // `fetchList` catches into `error`, so this only fires if that catch
    // itself throws.
    fetchList().catch(logger.error);
  }, [fetchList]);

  const statusCounts = useMemo(() => {
    const counts: Record<ProductionStatusFilter, number> = {
      ALL: fullList.length,
      LIVE: 0,
      PARTLY_LIVE: 0,
      NOT_LIVE_YET: 0,
      RETIRED: 0,
      UNKNOWN: 0,
    };
    fullList.forEach((processor) => counts[statusOf(processor)]++);
    return counts;
  }, [fullList]);

  const filteredList = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return fullList.filter(
      (processor) =>
        (statusFilter === 'ALL' || statusOf(processor) === statusFilter) &&
        (!term ||
          processor.name?.toLowerCase().includes(term) ||
          processor.code?.toLowerCase().includes(term)),
    );
  }, [fullList, searchTerm, statusFilter]);

  const list = useMemo(
    () => filteredList.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filteredList, page],
  );

  const resetPage = () => {
    setPage(0);
    setIsPageReset((prev) => !prev);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    resetPage();
  };

  const handleStatusFilter = (status: ProductionStatusFilter) => {
    setStatusFilter(status);
    resetPage();
  };

  return {
    isLoading,
    error,
    list,
    total: filteredList.length,
    setPage,
    isPageReset,
    isSearch: Boolean(searchTerm.trim()) || statusFilter !== 'ALL',
    searchTerm,
    handleSearch,
    statusFilter,
    handleStatusFilter,
    statusCounts,
  };
};

export default useProcessorList;
